import { CircleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet } from 'react-router';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { Button } from '@/components/ui/button';
import { useAdministrationScopes } from './place/AdministrationScopesProvider';

/**
 * The route gate of the administration area by place (FR-AD-3). A convenience, like every client
 * gate here: each place-scoped route is authorized again by the server (CST-AD-1).
 *
 * It distinguishes the three answers the scopes read can give, because they mean different things
 * to the person reading the screen: still asking, could not ask, and asked — you administer
 * nothing. Only the last one may say "no access".
 */
export function AdministrationAreaGate() {
  const { t } = useTranslation('administration');
  const { status, scopes, reload } = useAdministrationScopes();
  // The area opens on the active organization (FR-AD-1), so it waits for that as well: choosing
  // while it is still loading would open the broadest place instead, and for good.
  const contextStatus = useActiveContextOptional()?.status;

  if (status === 'loading' || contextStatus === 'loading') {
    return (
      <p role="status" className="text-muted-foreground p-6 text-sm">
        {t('area.checking')}
      </p>
    );
  }

  if (status === 'unavailable') {
    return (
      <div role="alert" className="flex flex-col items-start gap-3 p-6">
        <p className="flex items-center gap-2 text-sm">
          <CircleAlert aria-hidden="true" className="text-destructive size-4" />
          {t('area.unavailable')}
        </p>
        <Button variant="outline" onClick={reload}>
          {t('area.retry')}
        </Button>
      </div>
    );
  }

  if (scopes.length === 0) {
    return (
      <div className="flex flex-col items-start gap-3 p-6">
        <p className="text-sm">{t('area.notAdministrator')}</p>
        <Button asChild variant="outline">
          <Link to="/">{t('area.backHome')}</Link>
        </Button>
      </div>
    );
  }

  return <Outlet />;
}
